import axios from 'axios';
import { ROUTES } from "../routes";

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: parseInt(process.env.REACT_APP_TIMEOUT) || 60000,
  withCredentials: true, 
});

// Dedicated client for multipart/form-data requests (attachments).
// Do NOT set Content-Type here; axios/browser must set multipart boundaries.
const uploadClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: parseInt(process.env.REACT_APP_TIMEOUT) || 60000,
  withCredentials: true,
});

let isRedirecting = false;

let onUnauthorizedCallback = null;

/**
 * @param {Function} callback - Function to call on 401 error
 */
export const setUnauthorizedHandler = (callback) => {
  onUnauthorizedCallback = callback;
};

/**
 * Handle 401 Unauthorized errors by logging out and redirecting to login
 */
const handleUnauthorized = (error) => {
  if (isRedirecting) {
    return Promise.reject(error);
  }

  const currentPath = window.location.pathname;
  const requestUrl = error.config?.url || '';
  const isLoginPage = currentPath === ROUTES.LOGIN || currentPath.includes(ROUTES.LOGIN);
  const isAuthEndpoint = requestUrl.includes('/api/login/') || requestUrl.includes('/api/logout/');

  if (isLoginPage || isAuthEndpoint) {
    return Promise.reject(error);
  }

  isRedirecting = true;

  localStorage.removeItem('user');

  if (onUnauthorizedCallback && typeof onUnauthorizedCallback === 'function') {
    try {
      onUnauthorizedCallback();
    } catch (err) {
      console.error('Error in unauthorized callback:', err);
    }
  }

  console.warn('Session expired or unauthorized. Redirecting to login...');
  window.location.href = ROUTES.LOGIN;

  return Promise.reject(error);
};

const attachAuthInterceptors = (client) => {
  client.interceptors.request.use(
    (config) => {
      if (window.location.pathname === ROUTES.LOGIN) {
        isRedirecting = false;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && error.response.status === 401) {
        return handleUnauthorized(error);
      }
      const url = error.config?.url || '';
      if (error.response?.status === 500 && url.includes('/api/auth/user')) {
        return handleUnauthorized(error);
      }

      const isNotificationCount = url.includes('/api/notifications/count/');
      const isTicketsByAge = url.includes('/api/open-tickets/by-age/');
      const isTicketsByStatus = url.includes('/api/open-tickets/by-status/');
      const skipLog = isNotificationCount || isTicketsByAge || isTicketsByStatus;
      if (!skipLog) {
        if (error.response) {
          console.error('API Error:', error.response.status, error.response.data);
        } else if (error.request) {
          console.error('Network Error:', error.request);
        } else {
          console.error('Error:', error.message);
        }
      }
      return Promise.reject(error);
    }
  );
};

attachAuthInterceptors(apiClient);
attachAuthInterceptors(uploadClient);

const s3Client = axios.create({
  withCredentials: false,
});

export default apiClient;
export { API_BASE_URL, s3Client, uploadClient };

